import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  PAGE_TOP_PADDING,
  applyPageVisibility,
  buildPages,
  getBlockElements,
} from './paginatePolicy';

export default function usePolicyPagination() {
  const outerRef = useRef(null);
  const measureRef = useRef(null);
  const displayRef = useRef(null);
  const footerRef = useRef(null);
  const navigationRef = useRef(null);
  const finalActionsRef = useRef(null);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);

  const computePages = useCallback(() => {
    const outer = outerRef.current;
    const policyRoot = measureRef.current?.querySelector('.policy');
    const navigation = navigationRef.current;
    const footer = footerRef.current;
    if (!outer || !policyRoot || !navigation || !footer) return;

    const bottomPadding = parseFloat(window.getComputedStyle(footer).paddingBottom) || 0;
    const availableHeight =
      outer.clientHeight - navigation.offsetHeight - bottomPadding - PAGE_TOP_PADDING;
    if (availableHeight <= 0) return;

    const blocks = getBlockElements(policyRoot);
    if (blocks.length === 0) return;

    // Measure final actions even while hidden, keeping page boundaries stable
    // when the checkbox and acceptance buttons become visible.
    const finalHeight = availableHeight - (finalActionsRef.current?.offsetHeight || 0);
    const newPages = buildPages(blocks, availableHeight, finalHeight);
    setPages(newPages);
    setCurrentPage((prev) => Math.min(prev, newPages.length - 1));
  }, []);

  useLayoutEffect(() => {
    if (displayRef.current && pages.length > 0) {
      applyPageVisibility(displayRef.current, pages[currentPage] || []);
      displayRef.current.scrollTop = 0;
    }
  }, [currentPage, pages]);

  useEffect(() => {
    const observer = new ResizeObserver(computePages);
    [outerRef, navigationRef, finalActionsRef].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });
    const policyRoot = measureRef.current?.querySelector('.policy');
    if (policyRoot) observer.observe(policyRoot);
    computePages();
    return () => observer.disconnect();
  }, [computePages]);

  return {
    outerRef,
    measureRef,
    displayRef,
    footerRef,
    navigationRef,
    finalActionsRef,
    totalPages: pages.length,
    currentPage,
    setCurrentPage,
    isLastPage: pages.length > 0 && currentPage === pages.length - 1,
  };
}
